/* eslint-disable max-lines */
import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, EMPTY, exhaustMap, filter, forkJoin, map, mergeMap, of, switchMap, withLatestFrom } from 'rxjs';
import { CommodityType } from '@ui/aos-lib';

import { CustomerDetailsConsumptionActions } from './customer-details-consumption.actions';
import { ElectricFilterItem } from '../customer-details-consumption.types';
import { CustomerDetailsConsumptionApiService } from '../customer-details-consumption.api.service';
import {
  selectConsumptionReads,
  selectElectricFilters,
  selectElectricTableData,
  selectElectricTableState,
  selectPayload,
  selectSelectedChartRenderingModeTab,
  selectSelectedChartType,
  selectUnitOfMeasure
} from './customer-details-consumption.reducer';
import { buildUnitsOfMeasure, UnitsOfMeasure } from '../unit-of-measure/units-of-measure.utils';
import { ConsumptionWidgetService } from 'src/app/pages/csr/components/consumption-widget/consumption-widget.service';
import { ConsumptionResponse } from 'src/app/pages/csr/shared/consumption.types';
import { PreviousPeriodService } from 'src/app/pages/csr/shared/previous-period.serivce';
import { OrderDirection } from 'src/app/pages/equipment/devices/components/devices.models';
import { EventFieldName } from 'src/app/shared/entities/events';
import { CustomerDetailsConsumptionService } from '../customer-details-consumption.service';
import { ChartType } from '@portal/shared/components/device-details-chart/device-details-chart.types';
import {
  selectAccountNumber,
  selectCommodity,
  selectCurrentDeviceIds,
  selectDeviceSpecIdMap
} from '../../customer-consumption-filters/store/customer-consumption-filters.reducer';
import { MeterConsumptionTabContentService } from 'src/app/pages/equipment/devices/components/device-details/device-details-metrics/meter/meter-consumption-tab-content/meter-consumption-tab-content.service';
import { MeterConsumptionFieldName } from 'src/app/pages/equipment/devices/components/device-details/device-details-metrics/meter/meter-consumption-tab-content/meter-consumption-tab-content.types';
import { Filter } from 'src/app/shared/types/listing/filters.types';
import { mapTableSortToPayloadSort } from '@portal/shared/components/listing-page/helpers/map-sort-descriptor-to-sorting-state.helper';
import { ExportFacadeService } from 'src/app/services/utility/export-facade.service';
import { CustomerConsumptionFiltersActions } from '../../customer-consumption-filters/store/customer-consumption-filters.actions';
import { TabType } from '@portal/shared/types/tab.types';
import { EventsApiService } from 'src/app/services/api/events/events-api.service';

function buildElectricPayloadFilters(
  electricFilters: ElectricFilterItem[],
  deviceUID: string,
  startDateUtc: string,
  endDateUtc: string,
  accountNumber?: string,
  accountSubNumber?: string
): Filter[] {
  const dateFilter: Filter = {
    filterType: MeterConsumptionFieldName.dateTimeUTC,
    filteredValues: [startDateUtc, endDateUtc]
  };
  const deviceFilter: Filter = {
    filterType: MeterConsumptionFieldName.deviceUID,
    filteredValues: [deviceUID]
  };
  const accountFilters: Filter[] = [
    ...(accountNumber
      ? [{ filterType: MeterConsumptionFieldName.accountNumber, filteredValues: [accountNumber] }]
      : []),
    ...(accountSubNumber
      ? [{ filterType: MeterConsumptionFieldName.accountSubNumber, filteredValues: [accountSubNumber] }]
      : [])
  ];

  const filterEntries: Filter[] = electricFilters.flatMap(f => {
    if (f.filterType === MeterConsumptionFieldName.unitOfMeasure) {
      const uomValues: string[] = [];
      const multiplierValues: string[] = [];

      for (const item of f.selected) {
        const { multiplier, unitOfMeasure } = item as unknown as { multiplier?: string; unitOfMeasure?: string };

        if (multiplier) {
          uomValues.push(unitOfMeasure!);
          multiplierValues.push(multiplier);
        } else {
          uomValues.push(item.value as string);
        }
      }

      return [
        { filteredValues: uomValues, filterType: f.filterType },
        ...(multiplierValues.length
          ? [{ filteredValues: multiplierValues, filterType: MeterConsumptionFieldName.multiplier }]
          : [])
      ];
    }

    return [{ filterType: f.filterType, filteredValues: f.selected.map(s => s.value as string) }];
  });

  return [dateFilter, deviceFilter, ...accountFilters, ...filterEntries];
}

export const payloadChanged$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(CustomerDetailsConsumptionActions.payloadChanged),
      mergeMap(() => [CustomerDetailsConsumptionActions.getUnitOfMeasure()])
    ),
  { functional: true }
);

export const getConsumption$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    consumptionApiService = inject(CustomerDetailsConsumptionApiService),
    previousPeriodService = inject(PreviousPeriodService)
  ) =>
    actions$.pipe(
      ofType(
        CustomerDetailsConsumptionActions.getConsumption,
        CustomerDetailsConsumptionActions.payloadChanged,
        CustomerDetailsConsumptionActions.refresh
      ),
      withLatestFrom(
        store.select(selectPayload),
        store.select(selectSelectedChartType),
        store.select(selectAccountNumber)
      ),
      filter(([, payload, selectedChartType]) => {
        const hasPayload = !!payload;
        const hasIds = (payload?.deviceIds?.length ?? 0) > 0;
        const hasDate = payload?.date != null;
        const notTable = selectedChartType !== ChartType.Table;
        return hasPayload && hasIds && hasDate && notTable;
      }),
      exhaustMap(([, payload, , accountNumberValue]) => {
        const [accountNumber, accountSubNumber] = (accountNumberValue ?? '').split(';');
        return consumptionApiService
          .getConsumption({
            ...(accountNumber && { accountNumber }),
            ...(accountSubNumber && { accountSubNumber }),
            startDateUtc: payload!.date!.start,
            endDateUtc: payload!.date!.end,
            interval: payload!.interval,
            deviceUIDs: payload!.deviceIds!,
            comparisonPeriod: payload!.previousPeriod!
          })
          .pipe(
            switchMap((health: ConsumptionResponse) => {
              const preparedData = previousPeriodService.prepareConsumptionData(health);
              const consumption = previousPeriodService.overlapAxisDeviceHealthChart(preparedData[0], preparedData[1]);
              return [CustomerDetailsConsumptionActions.getConsumptionSuccess({ consumption })];
            }),
            catchError(() =>
              of(CustomerDetailsConsumptionActions.getConsumptionFailure({ error: 'Failed to load consumption' }))
            )
          );
      })
    ),
  { functional: true }
);

export const getConsumptionReads$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    customerDetailsService = inject(CustomerDetailsConsumptionService)
  ) =>
    actions$.pipe(
      ofType(
        CustomerDetailsConsumptionActions.getConsumptionReads,
        CustomerDetailsConsumptionActions.payloadChanged,
        CustomerDetailsConsumptionActions.refresh
      ),
      withLatestFrom(store.select(selectPayload), store.select(selectSelectedChartType)),
      filter(
        ([, payload, selectedChartType]) =>
          !!payload && !!payload.deviceIds?.length && payload.date !== null && selectedChartType === ChartType.Table
      ),
      switchMap(([, payload]) =>
        customerDetailsService.getTableData(payload).pipe(
          mergeMap(consumptionReads => [
            CustomerDetailsConsumptionActions.getConsumptionReadsSuccess({ consumptionReads })
          ]),
          catchError(() =>
            of(
              CustomerDetailsConsumptionActions.getConsumptionReadsFailure({
                error: 'Failed to load consumption reads'
              })
            )
          )
        )
      )
    ),
  { functional: true }
);

export const getConsumptionOverview$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    consumptionApiService = inject(CustomerDetailsConsumptionApiService),
    widgetService = inject(ConsumptionWidgetService)
  ) =>
    actions$.pipe(
      ofType(CustomerDetailsConsumptionActions.payloadChanged, CustomerDetailsConsumptionActions.refresh),
      withLatestFrom(store.select(selectPayload), store.select(selectAccountNumber)),
      filter(([, payload]) => !!payload && !!payload.deviceIds?.length && payload.date !== null),
      switchMap(([, payload, accountNumberValue]) => {
        const [accountNumber, accountSubNumber] = (accountNumberValue ?? '').split(';');
        return consumptionApiService
          .getConsumptionOverview({
            ...(accountNumber && { accountNumber }),
            ...(accountSubNumber && { accountSubNumber }),
            startDateUtc: payload!.date!.start,
            endDateUtc: payload!.date!.end,
            deviceUIDs: payload!.deviceIds!,
            comparisonPeriod: payload!.previousPeriod!
          })
          .pipe(
            mergeMap(consumptionOverview => [
              CustomerDetailsConsumptionActions.getConsumptionOverviewSuccess({
                consumptionOverview: widgetService.getOverviewWidgetConfig(consumptionOverview, true)
              })
            ]),
            catchError(() =>
              of(
                CustomerDetailsConsumptionActions.getConsumptionOverviewFailure({
                  error: 'Failed to load consumption overview'
                })
              )
            )
          );
      })
    ),
  { functional: true }
);

export const getConsumptionDaily$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    consumptionApiService = inject(CustomerDetailsConsumptionApiService),
    widgetService = inject(ConsumptionWidgetService)
  ) =>
    actions$.pipe(
      ofType(CustomerDetailsConsumptionActions.payloadChanged, CustomerDetailsConsumptionActions.refresh),
      withLatestFrom(store.select(selectPayload), store.select(selectAccountNumber)),
      filter(([, payload]) => !!payload && !!payload.deviceIds?.length),
      switchMap(([, payload, accountNumberValue]) => {
        const [accountNumber, accountSubNumber] = (accountNumberValue ?? '').split(';');
        return consumptionApiService
          .getConsumptionToday({
            ...(accountNumber && { accountNumber }),
            ...(accountSubNumber && { accountSubNumber }),
            deviceUIDs: payload!.deviceIds!
          })
          .pipe(
            mergeMap(consumptionDaily => [
              CustomerDetailsConsumptionActions.getConsumptionDailySuccess({
                consumptionDaily: widgetService.getTodayWidgetConfig(consumptionDaily)
              })
            ]),
            catchError(() =>
              of(
                CustomerDetailsConsumptionActions.getConsumptionDailyFailure({
                  error: 'Failed to load daily consumption'
                })
              )
            )
          );
      })
    ),
  { functional: true }
);

export const getEvents$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), eventsApiService = inject(EventsApiService)) =>
    actions$.pipe(
      ofType(
        CustomerDetailsConsumptionActions.getConsumptionEvents,
        CustomerDetailsConsumptionActions.payloadChanged,
        CustomerDetailsConsumptionActions.refresh
      ),
      withLatestFrom(
        store.select(selectPayload),
        store.select(selectCommodity),
        store.select(selectSelectedChartRenderingModeTab),
        store.select(selectSelectedChartType)
      ),
      filter(
        ([, payload, commodity, selectedChartRenderingModeTab, selectedChartType]) =>
          !!payload &&
          !!payload.deviceIds?.length &&
          commodity !== null &&
          payload.date !== null &&
          selectedChartRenderingModeTab === TabType.Events &&
          selectedChartType !== ChartType.Table
      ),
      switchMap(([, payload]) =>
        eventsApiService
          .getIntervalAggregatedEvents({
            filters: [
              {
                filterType: EventFieldName.LastDetectedUtc,
                filteredValues: [payload!.date!.start, payload!.date!.end]
              },
              {
                filterType: EventFieldName.DeviceUID,
                filteredValues: payload!.deviceIds!
              }
            ],
            interval: payload!.interval,
            sort: { columnName: EventFieldName.LastDetectedUtc, orderDirection: OrderDirection.DESC }
          })
          .pipe(
            map(events => CustomerDetailsConsumptionActions.getIntervalAggregatedEventsSuccess({ events })),
            catchError(() =>
              of(
                CustomerDetailsConsumptionActions.getIntervalAggregatedEventsFailure({
                  error: 'Failed to load events'
                })
              )
            )
          )
      )
    ),
  { functional: true }
);

export const export$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    customerDetailsService = inject(CustomerDetailsConsumptionService)
  ) =>
    actions$.pipe(
      ofType(CustomerDetailsConsumptionActions.exportFile),
      withLatestFrom(store.select(selectPayload), store.select(selectConsumptionReads)),
      filter(([, payload]) => !!payload && !!payload.deviceIds?.length && payload.date !== null),
      switchMap(([{ fileType, fileName }, payload, consumptionReads]) =>
        (consumptionReads !== null ? of(consumptionReads) : customerDetailsService.getTableData(payload)).pipe(
          switchMap(({ total }) =>
            customerDetailsService
              .export({ fileType, total, fileName })
              .pipe(switchMap(() => [CustomerDetailsConsumptionActions.exportFileSuccess()]))
          )
        )
      ),
      catchError(() => of(CustomerDetailsConsumptionActions.exportFileFailure({ error: 'Failed to export file' })))
    ),
  { functional: true }
);

export const getUnitOfMeasure$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    consumptionApiService = inject(CustomerDetailsConsumptionApiService)
  ) =>
    actions$.pipe(
      ofType(CustomerDetailsConsumptionActions.getUnitOfMeasure),
      withLatestFrom(
        store.select(selectPayload),
        store.select(selectCommodity),
        store.select(selectUnitOfMeasure),
        store.select(selectAccountNumber),
        store.select(selectCurrentDeviceIds),
        store.select(selectDeviceSpecIdMap)
      ),
      filter(
        ([, payload, commodity]) =>
          !!payload && !!payload.deviceIds?.length && payload.date !== null && commodity !== null
      ),
      switchMap(([, payload, commodity, unitOfMeasure, accountNumber, selectedDevices, deviceSpecIdMap]) => {
        const [accountNumberParsed, accountSubNumberParsed] = (accountNumber ?? '').split(';');
        const unitOfMeasure$ = consumptionApiService.getUnitOfMeasure({
          ...(accountNumberParsed && { accountNumber: accountNumberParsed }),
          ...(accountSubNumberParsed && { accountSubNumber: accountSubNumberParsed }),
          startDateUtc: payload!.date!.start,
          endDateUtc: payload!.date!.end,
          deviceUIDs: payload!.deviceIds!,
          comparisonPeriod: payload!.previousPeriod!
        });

        const originalUnitOfMeasure$ =
          selectedDevices?.length === 1
            ? consumptionApiService.getOriginalUoM(deviceSpecIdMap.get(selectedDevices[0])!)
            : of(null);

        return forkJoin([unitOfMeasure$, originalUnitOfMeasure$]).pipe(
          map(([{ unitOfMeasure }, originalUoM]) =>
            CustomerDetailsConsumptionActions.setUnitOfMeasureModel({
              model: buildUnitsOfMeasure({
                service: commodity as CommodityType,
                originalInputUnit: unitOfMeasure as UnitsOfMeasure,
                ...(originalUoM && { originalUnitOfMeasure: originalUoM as UnitsOfMeasure })
              }),
              ...(commodity && { commodity }),
              ...(accountNumber && { accountNumber })
            })
          ),
          catchError(() =>
            of(
              CustomerDetailsConsumptionActions.setUnitOfMeasureModel({
                model: unitOfMeasure?.unitOfMeasureModel,
                ...(commodity && { commodity }),
                ...(accountNumber && { accountNumber })
              })
            )
          )
        );
      })
    ),
  { functional: true }
);

export const loadElectricFilters$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    meterConsumptionTabContentService = inject(MeterConsumptionTabContentService)
  ) =>
    actions$.pipe(
      ofType(
        CustomerDetailsConsumptionActions.payloadChanged,
        CustomerDetailsConsumptionActions.refresh,
        CustomerConsumptionFiltersActions.buildFiltersByCommodity
      ),
      withLatestFrom(store.select(selectPayload), store.select(selectCommodity), store.select(selectAccountNumber)),
      filter(
        ([, payload, commodity]) =>
          !!payload && payload.date !== null && !!payload.deviceIds?.length && commodity === CommodityType.Electric
      ),
      switchMap(([, payload, , accountNumberValue]) => {
        const [accountNumber, accountSubNumber] = (accountNumberValue ?? '').split(';');
        return meterConsumptionTabContentService
          .getFilters({
            deviceUID: payload!.deviceIds![0],
            startDateUtc: payload!.date!.start,
            endDateUtc: payload!.date!.end,
            ...(accountNumber && { accountNumber }),
            ...(accountSubNumber && { accountSubNumber })
          })
          .pipe(
            map(filters =>
              CustomerDetailsConsumptionActions.setElectricFilters({
                filters: filters.map((f: ElectricFilterItem) => ({
                  filterType: f.filterType as string,
                  title: f.title,
                  items: f.items,
                  width: f.width,
                  selected: f.selected,
                  isMultiselect: f.isMultiselect,
                  isMultiTag: (f as { isMultiTag?: boolean }).isMultiTag
                }))
              })
            ),
            catchError(() => EMPTY)
          );
      })
    ),
  { functional: true }
);

export const loadElectricChartData$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    meterConsumptionTabContentService = inject(MeterConsumptionTabContentService)
  ) =>
    actions$.pipe(
      ofType(
        CustomerDetailsConsumptionActions.setElectricFilters,
        CustomerDetailsConsumptionActions.electricFilterChanged,
        CustomerDetailsConsumptionActions.setChartType
      ),
      withLatestFrom(
        store.select(selectPayload),
        store.select(selectCommodity),
        store.select(selectElectricFilters),
        store.select(selectSelectedChartType),
        store.select(selectAccountNumber)
      ),
      filter(
        ([, payload, commodity, electricFilters, selectedChartType]) =>
          commodity === CommodityType.Electric &&
          selectedChartType !== ChartType.Table &&
          !!payload?.date &&
          !!payload?.deviceIds?.length &&
          !!electricFilters?.length
      ),
      switchMap(([, payload, , electricFilters, , accountNumberValue]) => {
        const [accountNumber, accountSubNumber] = (accountNumberValue ?? '').split(';');
        const hasRequiredSelections = electricFilters!
          .filter(f => f.filterType !== MeterConsumptionFieldName.flowDirection)
          .every(f => f.selected.length > 0);
        if (!hasRequiredSelections) {
          return of(CustomerDetailsConsumptionActions.loadElectricChartDataSuccess({ healthData: [] }));
        }

        const flowDirFilter = electricFilters!.find(f => f.filterType === MeterConsumptionFieldName.flowDirection);
        if (flowDirFilter && flowDirFilter.selected.length === 0) {
          return of(CustomerDetailsConsumptionActions.loadElectricChartDataSuccess({ healthData: [] }));
        }

        const filters = buildElectricPayloadFilters(
          electricFilters!,
          payload!.deviceIds![0],
          payload!.date!.start,
          payload!.date!.end,
          accountNumber || undefined,
          accountSubNumber || undefined
        );
        const selectedFlowDirections = (flowDirFilter?.selected ?? []).map(s => (s.value as string).toLowerCase());

        return meterConsumptionTabContentService.getChartData({ filters }).pipe(
          map(healthData =>
            CustomerDetailsConsumptionActions.loadElectricChartDataSuccess({
              healthData: healthData.filter(hd => selectedFlowDirections.includes(hd.type))
            })
          ),
          catchError(() =>
            of(
              CustomerDetailsConsumptionActions.loadElectricChartDataFailure({
                error: 'Failed to load electric chart data'
              })
            )
          )
        );
      })
    ),
  { functional: true }
);

export const loadElectricTableData$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    meterConsumptionTabContentService = inject(MeterConsumptionTabContentService)
  ) =>
    actions$.pipe(
      ofType(
        CustomerDetailsConsumptionActions.setElectricFilters,
        CustomerDetailsConsumptionActions.electricFilterChanged,
        CustomerDetailsConsumptionActions.setChartType,
        CustomerDetailsConsumptionActions.setElectricTableState
      ),
      withLatestFrom(
        store.select(selectPayload),
        store.select(selectCommodity),
        store.select(selectElectricFilters),
        store.select(selectSelectedChartType),
        store.select(selectElectricTableState),
        store.select(selectAccountNumber)
      ),
      filter(
        ([, payload, commodity, electricFilters, selectedChartType]) =>
          commodity === CommodityType.Electric &&
          selectedChartType === ChartType.Table &&
          !!payload?.date &&
          !!payload?.deviceIds?.length &&
          !!electricFilters?.length
      ),
      switchMap(([, payload, , electricFilters, , electricTableState, accountNumberValue]) => {
        const [accountNumber, accountSubNumber] = (accountNumberValue ?? '').split(';');
        const hasRequiredSelections = electricFilters!
          .filter(f => f.filterType !== MeterConsumptionFieldName.flowDirection)
          .every(f => f.selected.length > 0);
        if (!hasRequiredSelections) {
          return of(
            CustomerDetailsConsumptionActions.loadElectricTableDataSuccess({ tableData: { data: [], total: 0 } })
          );
        }

        const flowDirFilter = electricFilters!.find(f => f.filterType === MeterConsumptionFieldName.flowDirection);
        if (flowDirFilter && flowDirFilter.selected.length === 0) {
          return of(
            CustomerDetailsConsumptionActions.loadElectricTableDataSuccess({ tableData: { data: [], total: 0 } })
          );
        }

        const filters = buildElectricPayloadFilters(
          electricFilters!,
          payload!.deviceIds![0],
          payload!.date!.start,
          payload!.date!.end,
          accountNumber || undefined,
          accountSubNumber || undefined
        );
        const sortDescriptor = electricTableState.sort?.[0];
        const sortColumnName =
          sortDescriptor?.field === 'date'
            ? MeterConsumptionFieldName.dateTimeUTC
            : (sortDescriptor?.field ?? MeterConsumptionFieldName.dateTimeUTC);

        return meterConsumptionTabContentService
          .getTableData({
            filters,
            sort: {
              columnName: sortColumnName,
              orderDirection: (sortDescriptor
                ? mapTableSortToPayloadSort(sortDescriptor)
                : OrderDirection.DESC) as unknown as OrderDirection
            },
            skip: electricTableState.skip,
            take: electricTableState.take
          })
          .pipe(
            map(({ items, totalCount }) =>
              CustomerDetailsConsumptionActions.loadElectricTableDataSuccess({
                tableData: { data: items, total: totalCount }
              })
            ),
            catchError(() =>
              of(
                CustomerDetailsConsumptionActions.loadElectricTableDataFailure({
                  error: 'Failed to load electric table data'
                })
              )
            )
          );
      })
    ),
  { functional: true }
);

export const exportElectricFile$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    meterConsumptionTabContentService = inject(MeterConsumptionTabContentService),
    exportFacade = inject(ExportFacadeService)
  ) =>
    actions$.pipe(
      ofType(CustomerDetailsConsumptionActions.exportElectricFile),
      withLatestFrom(
        store.select(selectPayload),
        store.select(selectElectricFilters),
        store.select(selectElectricTableData),
        store.select(selectElectricTableState),
        store.select(selectAccountNumber)
      ),
      filter(
        ([, payload, electricFilters]) => !!payload?.date && !!payload?.deviceIds?.length && !!electricFilters?.length
      ),
      switchMap(
        ([
          { fileType, fileName },
          payload,
          electricFilters,
          electricTableData,
          electricTableState,
          accountNumberValue
        ]) => {
          const [accountNumber, accountSubNumber] = (accountNumberValue ?? '').split(';');
          const exportLimit = 10000;
          const totalToExport = Math.min(electricTableData.total, exportLimit);
          const maxSizeRequest = 200;
          const filters = buildElectricPayloadFilters(
            electricFilters!,
            payload!.deviceIds![0],
            payload!.date!.start,
            payload!.date!.end,
            accountNumber || undefined,
            accountSubNumber || undefined
          );
          const sortDescriptor = electricTableState.sort?.[0];
          const sortColumnName =
            sortDescriptor?.field === 'date'
              ? MeterConsumptionFieldName.dateTimeUTC
              : (sortDescriptor?.field ?? MeterConsumptionFieldName.dateTimeUTC);
          const sort = {
            columnName: sortColumnName,
            orderDirection: (sortDescriptor
              ? mapTableSortToPayloadSort(sortDescriptor)
              : OrderDirection.DESC) as unknown as OrderDirection
          };

          const requests = Array.from({ length: Math.ceil(totalToExport / maxSizeRequest) }, (_, i) =>
            meterConsumptionTabContentService.getTableData({
              filters,
              sort,
              skip: i * maxSizeRequest,
              take: maxSizeRequest
            })
          );

          return forkJoin(requests.length ? requests : [of({ items: [], totalCount: 0 })]).pipe(
            switchMap(responses => {
              const allItems = responses.flatMap(r => r.items);
              const flowDirFilter = electricFilters!.find(
                f => f.filterType === MeterConsumptionFieldName.flowDirection
              );
              const selectedDirs = (flowDirFilter?.selected ?? []).map(s => (s.value as string).toLowerCase());
              const headers = ['DateTime', ...selectedDirs.map(d => d.charAt(0).toUpperCase() + d.slice(1))];
              const data = allItems.map(item => [
                item.date ?? '',
                ...selectedDirs.map(dir =>
                  item[dir as keyof typeof item] != null ? `${item[dir as keyof typeof item]}` : ''
                )
              ]);
              exportFacade.export({ fileType, options: { headers, data }, fileName });
              return [CustomerDetailsConsumptionActions.exportElectricFileSuccess()];
            }),
            catchError(() => [CustomerDetailsConsumptionActions.exportElectricFileSuccess()])
          );
        }
      )
    ),
  { functional: true }
);
